import { isoEn } from '@/app/_shared/lib/utils';
import type { LanguageIsoCode, SearchDirection } from '@/graphql/graphql';

export type PaginationArgs = {
    page?: number;
    pageSize?: number;
};

export type Range<T> = { min?: T | null; max?: T | null };

export type HeaderContextArgs = {
    locale?: string;
    skipAuth?: boolean;
};

export type HeaderContext = {
    skipAuth?: boolean;
    headers?: Record<string, string>;
};

export const toPagination = ({ page = 1, pageSize = 20 }: PaginationArgs) => ({
    limit: pageSize,
    offset: (page - 1) * pageSize,
});

export const toHeaderContext = ({ locale, skipAuth = false }: HeaderContextArgs): HeaderContext => {
    const context: HeaderContext = {};

    if (skipAuth) {
        context.skipAuth = true;
    }

    if (locale) {
        context.headers = { 'accept-language': locale.toLowerCase() };
    }

    return context;
};

export const getCurrentLocale = (locale?: string): LanguageIsoCode => {
    const explicitLocale = locale?.toLowerCase();
    if (explicitLocale) {
        return explicitLocale as LanguageIsoCode;
    }

    if (typeof document !== 'undefined') {
        const documentLocale = document.documentElement?.lang?.toLowerCase();
        if (documentLocale) {
            return documentLocale as LanguageIsoCode;
        }
    }

    return isoEn;
};

export const localizedMap = (
    value: string | null | undefined,
    locale?: string,
): Partial<Record<LanguageIsoCode, string>> => {
    if (!value) {
        return {};
    }

    const currentLocale = getCurrentLocale(locale);

    return {
        [currentLocale]: value,
    };
};

export const hasValue = (value: unknown): boolean =>
    value !== undefined && value !== null && !(typeof value === 'string' && value.trim() === '');

export const firstValue = (...values: unknown[]): unknown => values.find(hasValue);

export const asStringValue = (value: unknown): string | undefined => (hasValue(value) ? String(value) : undefined);

export const asStringArray = (value: unknown): string[] | undefined => {
    if (typeof value === 'string') {
        return value.trim() ? [value] : undefined;
    }

    if (!Array.isArray(value)) {
        return undefined;
    }

    return value.map(String).filter(Boolean);
};

export const asLowercaseStringArray = (value: unknown): string[] | undefined =>
    asStringArray(value)?.map((item) => item.toLowerCase());

export const asBoolean = (value: unknown): boolean | undefined => {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'string') {
        const normalizedValue = value.toLowerCase();

        if (normalizedValue === 'true') {
            return true;
        }

        if (normalizedValue === 'false') {
            return false;
        }
    }

    return undefined;
};

export const asNumber = (value: unknown): number | undefined => {
    if (!hasValue(value)) {
        return undefined;
    }

    const parsedValue = Number(value);

    return Number.isNaN(parsedValue) ? undefined : parsedValue;
};

export const normalizeDate = (value: unknown): string | undefined => {
    if (!hasValue(value)) {
        return undefined;
    }

    const date = String(value);

    return date.includes('T') ? date.split('T')[0] : date;
};

export const parseRange = <T extends number | string>(value: unknown): Range<T> | undefined => {
    if (!value) {
        return undefined;
    }

    if (typeof value === 'object') {
        return value as Range<T>;
    }

    if (typeof value !== 'string') {
        return undefined;
    }

    try {
        return JSON.parse(value) as Range<T>;
    } catch {
        return undefined;
    }
};

export const toNumberRange = (min?: unknown, max?: unknown): Range<number> | undefined => {
    const normalizedMin = min !== undefined && min !== null && min !== '' ? Number(min) : undefined;
    const normalizedMax = max !== undefined && max !== null && max !== '' ? Number(max) : undefined;

    return normalizedMin !== undefined || normalizedMax !== undefined
        ? {
              ...(normalizedMin !== undefined && { min: normalizedMin }),
              ...(normalizedMax !== undefined && { max: normalizedMax }),
          }
        : undefined;
};

export const toDateRange = (min?: unknown, max?: unknown): Range<string> | undefined => {
    const normalizedMin = normalizeDate(min);
    const normalizedMax = normalizeDate(max);

    return normalizedMin || normalizedMax
        ? {
              ...(normalizedMin && { min: normalizedMin }),
              ...(normalizedMax && { max: normalizedMax }),
          }
        : undefined;
};

export const toSearchDirection = (
    sortOrder?: 'asc' | 'desc' | 'random' | 'ASC' | 'DESC' | 'RANDOM',
): SearchDirection | undefined => {
    if (!sortOrder) {
        return undefined;
    }

    const normalizedSortOrder = String(sortOrder).toUpperCase();

    if (normalizedSortOrder === 'DESC') {
        return 'DESC' as SearchDirection;
    }

    if (normalizedSortOrder === 'ASC') {
        return 'ASC' as SearchDirection;
    }

    return undefined;
};
